import { useState, useCallback, useEffect } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_KNIVES = 5;   // 5 knife sets on the slitter from Aug 2026 - 1 set = 1 cut
const SCRAP_MM   = 100;  // under 100mm is scrap; at or above it goes on the offcut rack  // 4 knife sets on the slitter — 1 set = 1 cut
const MAX_STRIPS = 5;  // 5 strips only when they exactly fill the coil (edge-to-edge needs one cut fewer)
const MAX_LENGTH = 6000;

export const COIL_PRESETS = {
  coloursteel: { width: 1202, label: "Coloursteel", color: "#F5C518" },
  zinkalume:   { width: 1220, label: "Zinkalume",   color: "#7CB9E8" },
};

const COLORS = ["#F5C518","#E8A800","#FFD700","#D4A017","#B8860B","#FFC200","#F0B800","#E6AC00","#FFCF00","#F2BA00"];
// Flash Fold brand
const FF = {
  yellow:    "#F5C518",
  yellowDim: "#B8860B",
  black:     "#0A0A0A",
  darkGrey:  "#111111",
  midGrey:   "#1A1A1A",
  border:    "#2A2A2A",
  borderGold:"#F5C51844",
  text:      "#F0EBD8",
  textDim:   "#9A8F6E",
  textMuted: "#5A5040",
  red:       "#E53E3E",
  green:     "#38A169",
};

let _id = 1;
const uid  = () => `${_id++}`;
const fmt  = n  => Number(n).toLocaleString();
const fmtM = mm => (mm / 1000).toFixed(2) + "m";

const _gc = {}; let _gi = 0;
// Flash Fold brand — use yellow/gold shades for all girths
const GIRTH_SHADES = ["#F5C518","#E8A800","#D4A017","#C49010","#B8860B","#FFC200","#F0B800","#FFCF00","#E6AC00","#D4980A"];
const girthColor = g => { if (!_gc[g]) _gc[g] = GIRTH_SHADES[_gi++ % GIRTH_SHADES.length]; return _gc[g]; };

// ─── Storage ──────────────────────────────────────────────────────────────────
const SK = { history: "cco-history", stock: "cco-stock", settings: "cco-settings" };

async function storageGet(key, fallback) {
  try { const r = await window.storage.get(key); return r ? JSON.parse(r.value) : fallback; }
  catch { return fallback; }
}
async function storageSet(key, value) {
  try { await window.storage.set(key, JSON.stringify(value)); } catch {}
}

// ─── Optimiser ────────────────────────────────────────────────────────────────
export function optimise(allRows, coilWidth) {
  const pool = [];
  allRows.forEach(row => {
    if (row.length <= MAX_LENGTH) { pool.push({ ...row }); }
    else {
      const n = Math.ceil(row.length / MAX_LENGTH);
      pool.push({ ...row, length: Math.ceil(row.length / n), qty: row.qty * n, split: true, origLen: row.length });
    }
  });

  const groups = {};
  pool.forEach(r => {
    const k = `${r.girth}__${r.length}`;
    if (!groups[k]) groups[k] = { girth: r.girth, length: r.length, qty: 0, orders: {}, split: r.split, origLen: r.origLen };
    groups[k].qty += r.qty;
    groups[k].orders[r.orderRef] = (groups[k].orders[r.orderRef] || 0) + r.qty;
  });

  const cutGroups = [];
  Object.values(groups).sort((a,b) => b.girth - a.girth).forEach(item => {
    const maxFits   = Math.floor(coilWidth / item.girth);
    const maxStrips = Math.min(maxFits, (item.girth * (MAX_KNIVES + 1) === coilWidth ? MAX_KNIVES + 1 : MAX_KNIVES));
    const pMax      = Math.max(1, Math.floor(MAX_LENGTH / item.length));
    let remaining   = item.qty;
    // A girth WIDER than the coil made maxStrips 0. strips became 0, piecesThisRun became 0,
    // and `remaining` never came down - an infinite loop that filled the browser until the
    // tab was killed. It cannot be cut from this coil at all, so it is packed one per pass
    // and flagged, rather than spinning forever.
    const oversize = !(maxStrips >= 1);
    const stripsCap = Math.max(1, maxStrips);
    let guard = 0;
    while (remaining > 0 && guard++ < 10000) {
      // Width first: steel saved beats fewer knife setups
      const strips        = Math.max(1, Math.min(stripsCap, remaining));
      const piecesPerRun  = Math.max(1, Math.min(pMax, Math.ceil(remaining / strips)));
      const runLength     = piecesPerRun * item.length;
      const piecesThisRun = Math.max(1, Math.min(remaining, strips * piecesPerRun));
      cutGroups.push({ id: uid(), girth: item.girth, length: item.length, qty: piecesThisRun, strips, piecesPerRun, runLength, orders: item.orders, split: item.split, origLen: item.origLen, oversize });
      remaining -= piecesThisRun;
    }
  });

  cutGroups.sort((a,b) => (b.strips * b.girth) - (a.strips * a.girth));

  // ── Plan each pass against the steel that is ACTUALLY on the machine ──────────
  // It used to plan every pass against a full coil, and only chain onto a remnant when
  // the WHOLE next pass fitted. So a 574mm remnant that could hold three 157mm strips was
  // put on the rack and a fresh coil opened instead. Now a pass is filled from whatever
  // width is in front of it, splitting a batch across passes when that saves a coil.
  const queue = cutGroups.map(cg => Object.assign({}, cg));
  const passes = [];
  let avail = coilWidth;      // width currently on the machine
  let onRemnant = false;      // is that a remnant rather than a fresh coil?
  let guard = 0;

  while (queue.length && guard++ < 5000) {
    const pass = { id: uid(), strips: [], used: 0, plannedAvail: avail, plannedRemnant: onRemnant };
    let knives = 0;

    for (let k = 0; k < queue.length && knives < MAX_KNIVES; k++) {
      const cg = queue[k];
      const room = avail - pass.used;
      if (room < cg.girth) continue;
      // how many would fit on width alone, before any knife limit
      let take = Math.min(cg.strips, Math.floor(room / cg.girth));
      if (take <= 0) continue;
      // Both coil edges do the work of one cut when the strips fill the width EXACTLY,
      // so that case gets one more strip than there are knives.
      const fillsExactly = (pass.used + take * cg.girth) === avail;
      const cap = fillsExactly ? (MAX_KNIVES + 1 - knives) : (MAX_KNIVES - knives);
      if (take > cap) take = cap;
      if (take <= 0) continue;
      const pieces = Math.min(cg.qty, take * cg.piecesPerRun);
      pass.strips.push(Object.assign({}, cg, { strips: take, qty: pieces, runLength: cg.piecesPerRun * cg.length }));
      pass.used += take * cg.girth;
      knives += take;
      cg.strips -= take;
      cg.qty -= pieces;
      if (cg.qty <= 0 || cg.strips <= 0) { queue.splice(k, 1); k--; }
    }

    // a pass can pick up the same girth from two batches - show it as one line
    if (pass.strips.length > 1) {
      const merged = [];
      pass.strips.forEach(st => {
        const same = merged.find(m => m.girth === st.girth && m.length === st.length);
        if (same) { same.strips += st.strips; same.qty += st.qty; }
        else merged.push(st);
      });
      pass.strips = merged;
    }

    if (!pass.strips.length) {
      // nothing fits this remnant - put it aside and open a fresh coil
      if (onRemnant) { avail = coilWidth; onRemnant = false; continue; }
      break;                                   // cannot happen on a full coil; stop safely
    }
    passes.push(pass);

    const left = avail - pass.used;
    if (left >= SCRAP_MM && queue.length) { avail = left; onRemnant = true; }
    else { avail = coilWidth; onRemnant = false; }
  }

  // ── What happens to the width left after a pass ───────────────────────────────
  // Charl's process, and the reason the old numbers were wrong: the leftover is NOT
  // waste. Slit 4 x 150 off a 1220 coil and the 620mm that remains is a perfectly good
  // 620mm coil. It goes one of three ways:
  //   - more pieces still to cut, more than one  -> it goes through the line AGAIN,
  //     so the next pass starts from THAT width, not a fresh coil
  //   - exactly one piece left to cut            -> the folder's own slitter does it,
  //     no second slitting-line setup is planned
  //   - nothing left to cut                      -> >= SCRAP_MM it is an OFFCUT for the
  //     rack; only below that is it genuinely scrap
  passes.forEach((p, pi) => {
    p.num         = pi + 1;
    p.totalStrips = p.strips.reduce((s,st) => s + st.strips, 0);
    p.runLength   = Math.max(...p.strips.map(st => st.runLength));
    p.folderOk    = p.totalStrips === 1;

    // the width this pass actually ran on: a fresh coil, or the leftover carried forward
    // the planner already decided what this pass runs on - use its answer
    p.availWidth  = p.plannedAvail;
    p.fromRemnant = !!p.plannedRemnant;
    // Identify the actual steel. Two remnants can be the same width and look like one
    // piece on the sheet, which is exactly what confused the floor.
    p.remnantFrom = p.fromRemnant ? passes[pi-1].num : null;
    p.coilNo      = p.fromRemnant ? passes[pi-1].coilNo : (passes.slice(0, pi).filter(q => !q.fromRemnant).length + 1);
    p.leftover    = Math.max(0, p.availWidth - p.used);
    // 5 knife sets = 5 cuts = 5 strips plus a remainder, or 6 strips when they fill the
    // width exactly (both coil edges do the work of the fifth cut). Unchanged.
    p.knives      = p.totalStrips - (p.leftover === 0 ? 1 : 0);

    // can the NEXT pass be run off this leftover?
    const nextP   = passes[pi+1];
    const nextPcs = nextP ? nextP.strips.reduce((s,st) => s + st.qty, 0) : 0;
    // the next pass runs on this leftover only if the planner put it there
    if (nextP && nextP.plannedRemnant) {
      p.carryWidth = p.leftover;
      if (nextPcs === 1) nextP.folderSlit = true;   // one piece - use the folder's slitter
      else p.reslit = true;                          // back through the line on the remnant
    } else {
      p.carryWidth = 0;
    }

    // only what is not carried forward is judged offcut vs scrap
    const settled = p.carryWidth ? 0 : p.leftover;
    p.offcutWidth = settled >= SCRAP_MM ? settled : 0;
    p.waste       = settled >= SCRAP_MM ? 0 : settled;
    p.wastePct    = ((p.waste / p.availWidth) * 100).toFixed(1);
  });

  const totalPcs    = allRows.reduce((s,r) => s + r.qty, 0);
  const totalWaste  = passes.reduce((s,p) => s + p.waste, 0);
  const totalOffcut = passes.reduce((s,p) => s + (p.offcutWidth || 0), 0);
  const coilsUsed   = passes.filter(p => !p.fromRemnant).length;
  const avgWaste    = coilsUsed ? ((totalWaste / (coilsUsed * coilWidth)) * 100).toFixed(1) : "0";
  return { passes, totalPcs, totalWaste, totalOffcut, coilsUsed, avgWaste, coilWidth, scrapBelow: SCRAP_MM };
}

// ─── Small components ─────────────────────────────────────────────────────────
function Badge({ label, value, color }) {
  return (
    <div style={{ background:"#111111", border:`1px solid ${color?color+"33":"#2A2A2A"}`, borderRadius:8, padding:"10px 14px" }}>
      <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:17, fontWeight:800, color:color||"#F0EBD8" }}>{value}</div>
    </div>
  );
}

function Tag({ children, color }) {
  return (
    <span style={{ background:color+"22", color, border:`1px solid ${color}55`, borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:700, letterSpacing:1, whiteSpace:"nowrap", display:"inline-block" }}>
      {children}
    </span>
  );
}

function NumInput({ val, set, placeholder, w, err }) {
  return (
    <input type="number" value={val} min={0} placeholder={placeholder||""}
      onChange={e => set(e.target.value===""?"":Number(e.target.value))}
      style={{ width:w||"100%", background:"#0A0A0A", border:`1px solid ${err?"#F43F5E":"#2A2A2A"}`, borderRadius:5, padding:"7px 10px", color:"#F0EBD8", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
    />
  );
}

function Btn({ onClick, children, color="#2A2A2A", fg="#F0EBD8", ghost=false, disabled=false }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:"7px 16px", borderRadius:6, border:ghost?`1px solid ${color}55`:"none",
      background:ghost?"transparent":disabled?"#2A2A2A":color, color:disabled?"#5A5040":ghost?color:fg,
      cursor:disabled?"not-allowed":"pointer", fontSize:11, fontFamily:"inherit", fontWeight:700,
      letterSpacing:1, opacity:disabled?0.6:1, display:"inline-flex", alignItems:"center", gap:5,
    }}>{children}</button>
  );
}

function Step({ num, color, title, children }) {
  return (
    <div style={{ display:"flex", gap:14, marginBottom:16 }}>
      <div style={{ flexShrink:0, width:28, height:28, borderRadius:"50%", background:"#F5C51822", border:"2px solid #F5C518", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:"#F5C518", fontSize:13 }}>{num}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, color:"#F0EBD8", fontSize:12, paddingTop:4 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// ─── Coil cross-section diagram ───────────────────────────────────────────────
function CoilSection({ pass, coilWidth }) {
  const ticks = [];
  const step = Math.ceil(coilWidth / 8 / 50) * 50;
  for (let mm = 0; mm <= coilWidth; mm += step) ticks.push(mm);
  if (ticks[ticks.length-1] !== coilWidth) ticks.push(coilWidth);

  return (
    <div style={{ background:"#0A0A0A", padding:"16px 20px", borderBottom:"1px solid #2C2400" }}>
      {/* Dimension labels */}
      <div style={{ display:"flex", marginBottom:4 }}>
        {pass.strips.map((st,i) => {
          const col = girthColor(st.girth);
          const pct = (st.strips * st.girth / coilWidth * 100).toFixed(3);
          return (
            <div key={i} style={{ width:`${pct}%`, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:2 }}>
              <div style={{ width:"85%", height:1, background:col+"77", position:"relative" }}>
                <span style={{ position:"absolute", left:-3, top:-4, fontSize:8, color:col }}>◀</span>
                <span style={{ position:"absolute", right:-3, top:-4, fontSize:8, color:col }}>▶</span>
              </div>
              <div style={{ fontSize:9, color:col, fontWeight:700, marginTop:3 }}>{st.strips>1?`${st.strips}×`:""}{st.girth}mm</div>
            </div>
          );
        })}
        {pass.waste > 0 && (
          <div style={{ width:`${(pass.waste/coilWidth*100).toFixed(3)}%`, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:2 }}>
            <div style={{ fontSize:9, color:"#5A5040", marginTop:16 }}>{fmt(pass.waste)}mm</div>
          </div>
        )}
      </div>

      {/* Main bar */}
      <div style={{ display:"flex", height:80, borderRadius:6, overflow:"hidden", border:"1px solid #3A3000", boxShadow:"inset 0 1px 0 rgba(255,255,255,0.04)" }}>
        {pass.strips.map((st,i) => {
          const col = girthColor(st.girth);
          const pct = (st.strips * st.girth / coilWidth * 100).toFixed(4);
          const isLast = i === pass.strips.length - 1;
          return (
            <div key={i} style={{ width:`${pct}%`, position:"relative", background:`linear-gradient(180deg,${col}55 0%,${col}28 50%,${col}16 100%)`, borderRight:isLast?"none":"3px solid #070A0F", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:"35%", background:`linear-gradient(180deg,${col}22,transparent)` }} />
              {!isLast && <div style={{ position:"absolute", right:-2, top:0, width:3, height:"100%", background:"linear-gradient(180deg,#F5C518,#D4A017)", boxShadow:"0 0 8px #F5C51855", zIndex:2 }} />}
              <div style={{ textAlign:"center", zIndex:1 }}>
                {st.strips > 1 && <div style={{ fontSize:9, color:col+"99", marginBottom:2 }}>{st.strips} strips</div>}
                <div style={{ fontSize:13, fontWeight:700, color:col }}>{st.girth}mm</div>
                <div style={{ fontSize:9, color:col+"88", marginTop:2 }}>{st.qty} pcs</div>
              </div>
            </div>
          );
        })}
        {pass.waste > 0 && (
          <div style={{ width:`${(pass.waste/coilWidth*100).toFixed(4)}%`, background:"repeating-linear-gradient(-55deg,#1A2030 0,#1A2030 5px,#0D1320 5px,#0D1320 11px)", borderLeft:"1px solid #3A3000", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
            {pass.waste > coilWidth * 0.05 && <div style={{ fontSize:9, color:"#5A5040", textAlign:"center" }}><div>WASTE</div><div style={{ fontWeight:700 }}>{pass.wastePct}%</div></div>}
          </div>
        )}
      </div>

      {/* Knife markers */}
      {pass.knives > 0 && (
        <div style={{ position:"relative", height:28, marginTop:2 }}>
          {(() => { let cum=0; return pass.strips.slice(0,-1).map((st,i) => { cum+=st.strips*st.girth; return (
            <div key={i} style={{ position:"absolute", left:`${(cum/coilWidth*100).toFixed(3)}%`, transform:"translateX(-50%)", display:"flex", flexDirection:"column", alignItems:"center" }}>
              <div style={{ width:1, height:8, background:"#F5C518", boxShadow:"0 0 4px #F5C518" }} />
              <div style={{ background:"#F5C51822", border:"1px solid #F5C51866", borderRadius:3, padding:"1px 5px", fontSize:8, color:"#F5C518", fontWeight:700, whiteSpace:"nowrap" }}> K{i+1} · {fmt(cum)}mm</div>
            </div>
          ); }); })()}
        </div>
      )}

      {/* Ruler */}
      <div style={{ position:"relative", height:18, borderTop:"1px solid #2C2400", marginTop:pass.knives>0?0:6 }}>
        {ticks.map((mm,ti) => {
          const isFirst=ti===0, isLast=ti===ticks.length-1;
          return (
            <div key={ti} style={{ position:"absolute", left:`${(mm/coilWidth*100).toFixed(2)}%`, transform:isFirst?"none":isLast?"translateX(-100%)":"translateX(-50%)", display:"flex", flexDirection:"column", alignItems:"center" }}>
              <div style={{ width:1, height:4, background:"#333333" }} />
              <div style={{ fontSize:7, color:"#5A5040", whiteSpace:"nowrap" }}>{mm}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Shared table styles ──────────────────────────────────────────────────────
const TH = { padding:"9px 12px", fontSize:9, color:"#9A8F6E", letterSpacing:2, textAlign:"left", background:"#1A1A1A", borderBottom:"1px solid #2C2400", whiteSpace:"nowrap" };
const TD = alt => ({ padding:"9px 12px", borderBottom:"1px solid #0D1117", background:alt?"#161616":"transparent", verticalAlign:"middle" });

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  // ── Core state
  const [view, setView]           = useState("input");
  const [coilKey, setCoilKey]     = useState("coloursteel");
  const [customW, setCustomW]     = useState("");
  const [gauge, setGauge]         = useState("");
  const [kgPerM, setKgPerM]       = useState("");
  const [orders, setOrders]       = useState([{ id: uid(), ref: "Order 001", rows: [{ id: uid(), girth:"", length:"", qty:"" }] }]);
  const [result, setResult]       = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [jobName, setJobName]     = useState("");

  // ── History state
  const [history, setHistory]     = useState([]);
  const [histRange, setHistRange] = useState({ from:"", to:"" });

  // ── Stock state
  const [stock, setStock]               = useState([]);
  const [stockSettings, setStockSettings] = useState({ warnKg: 120 });
  const [showModal, setShowModal]       = useState(false);
  const [pendingJob, setPendingJob]     = useState(null);
  const [newCoil, setNewCoil]           = useState({ colour:"", type:"coloursteel", gauge:"", width:"", weightKg:"", notes:"" });

  // ── Load persisted data
  useEffect(() => {
    storageGet(SK.history, []).then(setHistory);
    storageGet(SK.stock, []).then(setStock);
    storageGet(SK.settings, { warnKg:120 }).then(setStockSettings);
  }, []);

  // ── Derived values
  const coilWidth   = customW ? Number(customW) : COIL_PRESETS[coilKey].width;
  const accent      = customW ? "#C4A1FF" : COIL_PRESETS[coilKey].color;
  const kgRate      = Number(kgPerM) || 0;
  const kgPerMmSq   = kgRate > 0 ? kgRate / coilWidth / 1000 : 0;
  const weightKg    = (wMm, lMm) => kgPerMmSq > 0 ? wMm * lMm * kgPerMmSq : null;
  const fmtKg       = kg => kg !== null && kg !== undefined ? kg.toFixed(2) + "kg" : "—";

  const toggle = k => setCollapsed(p => ({ ...p, [k]: !p[k] }));

  // ── Order management
  const addOrder = () => setOrders(p => [...p, { id:uid(), ref:`Order ${String(p.length+1).padStart(3,"0")}`, rows:[{ id:uid(), girth:"", length:"", qty:"" }] }]);
  const delOrder = id => setOrders(p => p.filter(o => o.id !== id));
  const setRef   = (oid,v) => setOrders(p => p.map(o => o.id===oid?{...o,ref:v}:o));
  const addRow   = oid => setOrders(p => p.map(o => o.id===oid?{...o,rows:[...o.rows,{id:uid(),girth:"",length:"",qty:""}]}:o));
  const delRow   = (oid,rid) => setOrders(p => p.map(o => o.id===oid?{...o,rows:o.rows.filter(r=>r.id!==rid)}:o));
  const setCell  = (oid,rid,f,v) => setOrders(p => p.map(o => o.id===oid?{...o,rows:o.rows.map(r=>r.id===rid?{...r,[f]:v}:r)}:o));

  const allValid = orders.flatMap(o => o.rows
    .filter(r => Number(r.girth)>0 && Number(r.length)>0 && Number(r.qty)>0)
    .map(r => ({ ...r, orderRef:o.ref, girth:Number(r.girth), length:Number(r.length), qty:Number(r.qty) }))
  );

  // ── Run optimiser
  const run = useCallback(() => {
    const errs = allValid.filter(r => r.girth > coilWidth);
    if (errs.length) { alert(`Girth ${errs[0].girth}mm exceeds coil width ${coilWidth}mm`); return; }
    if (!allValid.length) { alert("Add at least one part to optimise."); return; }
    setResult(optimise(allValid, coilWidth));
    setView("results");
    setCollapsed({});
  }, [allValid, coilWidth]);

  const clearAll = () => { setOrders([]); setResult(null); setView("input"); };

  // ── Save job
  const calcWeights = () => {
    if (!result) return { pieceKg: null, wasteKg: null, totalKg: null };
    const pieceKg = kgRate > 0 ? result.passes.reduce((s,p) => s + p.strips.reduce((ss,st) => ss + (weightKg(st.girth, st.length*st.qty)||0), 0), 0) : null;
    const wasteKg = kgRate > 0 ? result.passes.reduce((s,p) => s + (weightKg(p.waste, p.runLength)||0), 0) : null;
    const totalKg = pieceKg !== null ? pieceKg + wasteKg : null;
    return { pieceKg, wasteKg, totalKg };
  };

  const handleSaveJob = () => {
    if (!result) return;
    const { pieceKg, wasteKg, totalKg } = calcWeights();
    const matchingCoils = kgRate > 0 ? stock.filter(c => c.type===coilKey && Number(c.gauge)===Number(gauge) && c.remaining > 0).sort((a,b) => a.remaining - b.remaining) : [];
    setPendingJob({ jobName: jobName||`Job ${new Date().toLocaleDateString("en-AU")}`, pieceKg, wasteKg, totalKg, matchingCoils });
    setShowModal(true);
  };

  const commitSaveJob = async (coilId) => {
    const { pieceKg, wasteKg, totalKg } = calcWeights();
    const name = pendingJob.jobName;
    const record = {
      id: uid(), name, date: new Date().toISOString(),
      coilType: customW?`Custom ${coilWidth}mm`:COIL_PRESETS[coilKey].label, coilWidth,
      gauge: gauge||null, kgPerM: kgRate||null,
      passes: result.passes.length, totalPcs: result.totalPcs,
      totalWasteMm: result.totalWaste, avgWastePct: result.avgWaste,
      totalPieceKg: pieceKg, totalWasteKg: wasteKg,
    };
    const newHistory = [record, ...history];
    setHistory(newHistory);
    await storageSet(SK.history, newHistory);

    if (coilId) {
      const newStock = stock.map(c => {
        if (c.id !== coilId) return c;
        return { ...c, remaining: Math.max(0, c.remaining - totalKg), deductions: [...(c.deductions||[]), { date: new Date().toISOString(), jobName: name, kg: totalKg }] };
      });
      setStock(newStock);
      await storageSet(SK.stock, newStock);
    }

    setShowModal(false); setPendingJob(null); setJobName("");
    alert(`✓ "${name}" saved${coilId?" and stock updated":""}.`);
  };

  // ── Stock management
  const addCoil = async () => {
    if (!newCoil.colour || !newCoil.gauge || !newCoil.weightKg) { alert("Enter colour, gauge and weight."); return; }
    const coil = { id:uid(), colour:newCoil.colour.trim(), type:newCoil.type, gauge:Number(newCoil.gauge), width:Number(newCoil.width)||COIL_PRESETS[newCoil.type].width, weightKg:Number(newCoil.weightKg), remaining:Number(newCoil.weightKg), notes:newCoil.notes.trim(), addedDate:new Date().toISOString(), deductions:[] };
    const newStock = [coil, ...stock];
    setStock(newStock); await storageSet(SK.stock, newStock);
    setNewCoil({ colour:"", type:"coloursteel", gauge:"", width:"", weightKg:"", notes:"" });
  };

  const removeCoil = async id => {
    if (!window.confirm("Remove this coil from stock?")) return;
    const newStock = stock.filter(c => c.id !== id);
    setStock(newStock); await storageSet(SK.stock, newStock);
  };

  const updateSettings = async s => { setStockSettings(s); await storageSet(SK.settings, s); };

  const deleteRecord = async id => {
    const updated = history.filter(r => r.id !== id);
    setHistory(updated); await storageSet(SK.history, updated);
  };

  const lowStockCount = stock.filter(c => c.remaining > 0 && c.remaining <= stockSettings.warnKg).length;

  // ─────────────────────────────────────────────────────────────────────────────
  // INPUT VIEW
  // ─────────────────────────────────────────────────────────────────────────────
  const InputView = (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div style={{ fontSize:11, color:"#9A8F6E" }}>
          Coil: <strong style={{ color:accent }}>{coilWidth}mm</strong>{" ·  "}
          Parts: <strong style={{ color:"#F0EBD8" }}>{allValid.length}</strong>{" ·  "}
          <span style={{ color:"#F5C518" }}>Max 5 strips · Max 6000mm</span>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn onClick={addOrder} color={accent} fg="#000">＋ NEW ORDER</Btn>
          <Btn onClick={clearAll} color="#F43F5E" ghost disabled={!orders.length}>✕ CLEAR</Btn>
          <Btn onClick={run} color={accent} fg="#000" disabled={!allValid.length}>▶ OPTIMISE</Btn>
        </div>
      </div>

      <div style={{ background:"#111111", border:"1px solid #F5C51833", borderRadius:8, padding:"9px 16px", marginBottom:16, fontSize:10, color:"#9A8F6E", display:"flex", flexWrap:"wrap", gap:14 }}>
        <span style={{ color:"#F5C518", fontWeight:700 }}> MACHINE LIMITS</span>
        <span>Max <strong style={{ color:"#F0EBD8" }}>5 strips</strong> per pass</span>
        <span>Max length <strong style={{ color:"#F0EBD8" }}>6000mm</strong> — longer auto-split</span>
        <span>Cross-cut: <strong style={{ color:"#F0EBD8" }}>full coil width only</strong></span>
        <span>Folder slitter: <strong style={{ color:"#F0EBD8" }}>1 knife, single strip</strong></span>
      </div>

      {orders.length === 0 && (
        <div style={{ textAlign:"center", padding:"80px 0", color:"#2A2A2A" }}>
          <div style={{ fontSize:52 }}>⊞</div>
          <div style={{ letterSpacing:3, fontSize:11, marginTop:12 }}>NO ORDERS — CLICK ＋ NEW ORDER</div>
        </div>
      )}

      {orders.map((order, oi) => {
        const oc = COLORS[oi % COLORS.length];
        const valid = order.rows.filter(r => r.girth && r.length && r.qty);
        const isCol = collapsed[order.id];
        return (
          <div key={order.id} style={{ background:"#111111", border:`1px solid ${oc}44`, borderRadius:10, overflow:"hidden", marginBottom:12 }}>
            <div style={{ background:"#1A1A1A", borderBottom:"1px solid #2C2400", padding:"10px 16px", display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
                <Tag color={oc}>{oi+1}</Tag>
                <input value={order.ref} onChange={e=>setRef(order.id,e.target.value)} placeholder="Order reference..."
                  style={{ background:"transparent", border:"none", color:"#F0EBD8", fontSize:14, fontFamily:"inherit", fontWeight:700, outline:"none", width:230, maxWidth:"55vw" }} />
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ color:"#9A8F6E", fontSize:11 }}>{valid.reduce((s,r)=>s+Number(r.qty),0)} pcs</span>
                <Btn onClick={()=>addRow(order.id)}>＋ ROW</Btn>
                <Btn onClick={()=>toggle(order.id)}>{isCol?"▼":"▲"}</Btn>
                <Btn onClick={()=>delOrder(order.id)} color="#F43F5E" ghost>✕</Btn>
              </div>
            </div>
            {!isCol && (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...TH, width:32 }}>#</th>
                      <th style={TH}>GIRTH (mm)</th>
                      <th style={TH}>LENGTH (mm)</th>
                      <th style={TH}>QTY</th>
                      <th style={TH}>STATUS</th>
                      <th style={{ ...TH, width:32 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.rows.map((row,ri) => {
                      const g=Number(row.girth), l=Number(row.length), q=Number(row.qty);
                      const gErr=g>coilWidth, lWarn=l>MAX_LENGTH, ok=g>0&&l>0&&q>0&&!gErr;
                      return (
                        <tr key={row.id}>
                          <td style={TD(ri%2)}><span style={{ color:"#5A5040" }}>{ri+1}</span></td>
                          <td style={TD(ri%2)}>
                            <NumInput val={row.girth} set={v=>setCell(order.id,row.id,"girth",v)} placeholder="e.g. 300" err={gErr} />
                            {gErr && <div style={{ color:"#F43F5E", fontSize:9, marginTop:2 }}>Exceeds coil width</div>}
                          </td>
                          <td style={TD(ri%2)}>
                            <NumInput val={row.length} set={v=>setCell(order.id,row.id,"length",v)} placeholder="e.g. 3600" err={lWarn} />
                            {lWarn && <div style={{ color:"#F5C518", fontSize:9, marginTop:2 }}>Over 6m — will auto-split</div>}
                          </td>
                          <td style={TD(ri%2)}><NumInput val={row.qty} set={v=>setCell(order.id,row.id,"qty",v)} placeholder="1" w={72} /></td>
                          <td style={TD(ri%2)}>
                            {gErr?<Tag color="#F43F5E">⚠ TOO WIDE</Tag>:lWarn?<Tag color="#F5C518"> AUTO-SPLIT</Tag>:ok?<Tag color="#38A169">✓ OK</Tag>:null}
                          </td>
                          <td style={TD(ri%2)}>
                            <button onClick={()=>delRow(order.id,row.id)} style={{ background:"none", border:"none", color:"#ff1e1e", fontWeight:900, cursor:"pointer", fontSize:20, padding:0 }}
                              onMouseEnter={e=>e.target.style.color="#F43F5E"} onMouseLeave={e=>e.target.style.color="#ff1e1e"}>×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // RESULTS VIEW
  // ─────────────────────────────────────────────────────────────────────────────
  const ResultsView = result && (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <Btn onClick={()=>setView("input")}>← EDIT ORDERS</Btn>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <input value={jobName} onChange={e=>setJobName(e.target.value)} placeholder="Job name (optional)..."
            style={{ background:"#111111", border:"1px solid #4ADE8044", borderRadius:6, padding:"7px 12px", color:"#F0EBD8", fontSize:12, fontFamily:"inherit", outline:"none", width:220 }} />
          <Btn onClick={handleSaveJob} color="#38A169" fg="#000"> SAVE JOB</Btn>
          <Btn onClick={run} color={accent} fg="#000">↻ RE-OPTIMISE</Btn>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginBottom:24 }}>
        <Badge label="COIL" value={`${coilWidth}mm`} color={accent} />
        <Badge label="PASSES" value={result.passes.length} color="#F5C518" />
        <Badge label="TOTAL PIECES" value={result.totalPcs} />
        <Badge label="AVG WASTE" value={`${result.avgWaste}%`} color={parseFloat(result.avgWaste)>15?"#F43F5E":"#38A169"} />
        {kgRate>0 && <Badge label="PIECE WEIGHT" value={fmtKg(calcWeights().pieceKg)} color="#38A169" />}
        {kgRate>0 && <Badge label="WASTE WEIGHT" value={fmtKg(calcWeights().wasteKg)} color="#F43F5E" />}
      </div>

      {/* Passes */}
      {result.passes.map((pass,pi) => {
        const isCol = collapsed[`p${pi}`];
        const badWaste = parseFloat(pass.wastePct) > 15;
        const passKg = kgRate>0 ? pass.strips.reduce((s,st)=>s+(weightKg(st.girth,st.length*st.qty)||0),0) : null;
        const wasteKg = kgRate>0 ? weightKg(pass.waste,pass.runLength) : null;

        return (
          <div key={pass.id} style={{ background:"#111111", border:`1px solid ${accent}33`, borderRadius:12, overflow:"hidden", marginBottom:20 }}>
            <div onClick={()=>toggle(`p${pi}`)} style={{ background:"#1A1A1A", padding:"14px 20px", cursor:"pointer", userSelect:"none", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                <div style={{ width:44, height:44, borderRadius:10, background:accent+"22", border:`2px solid ${accent}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:800, color:accent, flexShrink:0 }}>{pass.num}</div>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:"#F0EBD8" }}>PASS {pass.num} — Set {pass.totalStrips} strip{pass.totalStrips!==1?"s":""} on slitter</div>
                  <div style={{ fontSize:11, color:"#9A8F6E", marginTop:2 }}>
                    Run <strong style={{ color:"#F0EBD8" }}>{fmtM(pass.runLength)}</strong> · <strong style={{ color:"#F5C518" }}>{pass.knives} knife cut{pass.knives!==1?"s":""}</strong> <span style={{opacity:0.6,fontSize:11}}>(max 4)</span>
                    {pass.folderOk && <span style={{ color:"#C084FC" }}> ·  Folder slitter OK</span>}
                    <span style={{ color:badWaste?"#F43F5E":"#38A169" }}> · {fmt(pass.waste)}mm waste ({pass.wastePct}%)</span>
                    {kgRate>0 && <span style={{ color:"#38A169" }}> · {fmtKg(passKg)} parts · <span style={{ color:"#F43F5E" }}>{fmtKg(wasteKg)} waste</span></span>}
                  </div>
                </div>
              </div>
              <span style={{ color:"#9A8F6E", fontSize:18 }}>{isCol?"▼":"▲"}</span>
            </div>

            {!isCol && (
              <div>
                <CoilSection pass={pass} coilWidth={coilWidth} />

                <div style={{ padding:"20px 20px 8px" }}>
                  <div style={{ fontSize:10, color:"#9A8F6E", letterSpacing:3, marginBottom:14 }}>STEP-BY-STEP CUTTING INSTRUCTIONS</div>

                  {/* Step 1 — knife positions */}
                  <Step num={1} color="#F5C518" title="Set knife positions on slitter">
                    <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:8 }}>
                      {(() => { let cum=0; return pass.strips.map((st,si) => {
                        const prev=cum; cum+=st.strips*st.girth; const col=girthColor(st.girth);
                        return (
                          <div key={si} style={{ background:"#111111", border:`1px solid ${col}44`, borderLeft:`3px solid ${col}`, borderRadius:6, padding:"8px 14px", minWidth:160 }}>
                            <div style={{ fontSize:10, color:"#9A8F6E", marginBottom:4 }}>Strip{st.strips>1?`s ${si*st.strips+1}–${(si+1)*st.strips}`:` ${si+1}`}</div>
                            <div style={{ fontSize:13, fontWeight:700, color:col }}>{st.girth}mm girth</div>
                            <div style={{ fontSize:10, color:"#9A8F6E", marginTop:3 }}>Position: <strong style={{ color:"#F0EBD8" }}>{fmt(prev)}mm – {fmt(cum)}mm</strong></div>
                            {st.strips>1 && <div style={{ fontSize:10, color:"#9A8F6E", marginTop:2 }}>{st.strips} strips side by side</div>}
                          </div>
                        );
                      }); })()}
                      {pass.waste>0 && (
                        <div style={{ background:"#111111", border:"1px solid #2C2400", borderLeft:"3px solid #3A3000", borderRadius:6, padding:"8px 14px", minWidth:120 }}>
                          <div style={{ fontSize:10, color:"#5A5040", marginBottom:4 }}>Waste edge</div>
                          <div style={{ fontSize:13, fontWeight:700, color:badWaste?"#F43F5E":"#5A5040" }}>{fmt(pass.waste)}mm</div>
                          <div style={{ fontSize:10, color:"#5A5040" }}>{pass.wastePct}% of coil</div>
                          {kgRate>0 && <div style={{ fontSize:10, color:"#F43F5E", marginTop:2 }}>{fmtKg(wasteKg)}</div>}
                        </div>
                      )}
                    </div>
                  </Step>

                  {/* Step 2 — feed coil */}
                  <Step num={2} color="#7CB9E8" title={`Feed ${fmtM(pass.runLength)} of coil through slitter`}>
                    <div style={{ fontSize:11, color:"#9A8F6E", marginTop:6 }}>
                      Run length: <strong style={{ color:"#F0EBD8", fontSize:14 }}>{fmt(pass.runLength)}mm = {fmtM(pass.runLength)}</strong>
                      {pass.strips.some(st=>st.piecesPerRun>1) && (
                        <span style={{ marginLeft:12, color:"#38A169" }}>✓ Cross-cut at {pass.strips.filter(st=>st.piecesPerRun>1).map(st=>fmtM(st.length)).join(", ")} for multiple pieces per strip</span>
                      )}
                    </div>
                  </Step>

                  {/* Step 3 — pieces produced */}
                  <Step num={3} color="#38A169" title="Pieces produced from this pass">
                    <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:8 }}>
                      {pass.strips.map((st,si) => {
                        const col = girthColor(st.girth);
                        const ords = Object.entries(st.orders||{[st.orderRef]:st.qty});
                        const pcKg = weightKg(st.girth, st.length*st.qty);
                        return (
                          <div key={si} style={{ background:"#111111", border:`1px solid ${col}33`, borderLeft:`4px solid ${col}`, borderRadius:8, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                              <div style={{ textAlign:"center" }}>
                                <div style={{ fontSize:28, fontWeight:800, color:col, lineHeight:1 }}>{st.qty}</div>
                                <div style={{ fontSize:9, color:col+"88", letterSpacing:1 }}>PIECES</div>
                              </div>
                              <div>
                                <div style={{ fontSize:15, fontWeight:700, color:"#F0EBD8" }}>{fmt(st.girth)}mm × {fmt(st.length)}mm</div>
                                <div style={{ fontSize:11, color:"#9A8F6E", marginTop:2 }}>{fmtM(st.girth)} girth · {fmtM(st.length)} long</div>
                                {st.piecesPerRun>1 && <div style={{ fontSize:10, color:"#38A169", marginTop:2 }}>✓ {st.piecesPerRun} pieces per strip (cross-cut from {fmtM(st.runLength)} run)</div>}
                                {st.split && <div style={{ fontSize:10, color:"#F5C518", marginTop:2 }}> Split from original {fmt(st.origLen)}mm</div>}
                                {kgRate>0 && <div style={{ fontSize:10, color:"#38A169", marginTop:3 }}>Per piece: <strong>{fmtKg(weightKg(st.girth,st.length))}</strong> · Total: <strong>{fmtKg(pcKg)}</strong></div>}
                              </div>
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"flex-end" }}>
                              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:2 }}>FOR ORDER</div>
                              {ords.map(([ref,qty]) => (
                                <div key={ref} style={{ display:"flex", alignItems:"center", gap:6 }}>
                                  <span style={{ fontSize:12, fontWeight:700, color:"#F0EBD8" }}>{ref}</span>
                                  <span style={{ background:col+"22", color:col, border:`1px solid ${col}44`, borderRadius:4, padding:"1px 8px", fontWeight:700, fontSize:12 }}>×{qty}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Step>

                  {/* Waste summary */}
                  <div style={{ background:badWaste?"#110A0A":"#0A110A", border:`1px solid ${badWaste?"#F43F5E44":"#4ADE8044"}`, borderRadius:8, padding:"10px 16px", marginBottom:16, display:"flex", gap:20, flexWrap:"wrap", alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:3 }}>WIDTH WASTE THIS PASS</div>
                      <div style={{ fontSize:16, fontWeight:700, color:badWaste?"#F43F5E":"#38A169" }}>{fmt(pass.waste)}mm ({pass.wastePct}%)</div>
                      <div style={{ fontSize:10, color:"#9A8F6E", marginTop:2 }}>{coilWidth}mm coil — {fmt(pass.used)}mm used</div>
                      {kgRate>0 && <div style={{ fontSize:10, marginTop:4 }}><span style={{ color:"#38A169" }}>Parts: {fmtKg(passKg)}</span> · <span style={{ color:"#F43F5E" }}>Waste: {fmtKg(wasteKg)}</span></div>}
                    </div>
                    <div style={{ flex:1, minWidth:160 }}>
                      <div style={{ display:"flex", height:8, borderRadius:4, overflow:"hidden", background:"#2A2A2A" }}>
                        <div style={{ width:`${(pass.used/coilWidth*100).toFixed(2)}%`, background:accent+"99" }} />
                        <div style={{ width:`${(pass.waste/coilWidth*100).toFixed(2)}%`, background:badWaste?"#F43F5E88":"#38A16966" }} />
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:8, color:"#5A5040", marginTop:3 }}>
                        <span>Used {fmt(pass.used)}mm</span><span>Waste {fmt(pass.waste)}mm</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Summary table */}
      <div style={{ background:"#111111", border:"1px solid #2C2400", borderRadius:12, overflow:"hidden" }}>
        <div style={{ background:"#1A1A1A", borderBottom:"1px solid #2C2400", padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontWeight:700, fontSize:13, letterSpacing:1 }}>◈ FULL CUTTING SUMMARY</span>
          <span style={{ color:"#9A8F6E", fontSize:11 }}>{result.totalPcs} pieces · {result.passes.length} passes</span>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr>
                <th style={TH}>PASS</th><th style={TH}>ORDER</th><th style={TH}>GIRTH</th>
                <th style={TH}>LENGTH</th><th style={TH}>COIL RUN</th><th style={TH}>PCS/STRIP</th>
                <th style={TH}>STRIPS</th><th style={TH}>TOTAL PCS</th>
                {kgRate>0&&<th style={TH}>PIECE WEIGHT</th>}
                <th style={TH}>WIDTH WASTE</th>
              </tr>
            </thead>
            <tbody>
              {result.passes.flatMap((pass,pi) =>
                pass.strips.map((st,si) => {
                  const col=girthColor(st.girth), isFirst=si===0;
                  const ords=Object.entries(st.orders||{[st.orderRef]:st.qty});
                  return (
                    <tr key={`${pi}_${si}`}>
                      <td style={TD((pi+si)%2)}>
                        {isFirst
                          ? <div style={{ width:28, height:28, borderRadius:6, background:accent+"22", border:`1.5px solid ${accent}`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:accent }}>{pass.num}</div>
                          : <div style={{ width:28, height:28, borderRadius:6, border:`1px dashed ${accent}33`, display:"flex", alignItems:"center", justifyContent:"center", color:accent+"44", fontSize:11 }}>{pass.num}</div>}
                      </td>
                      <td style={TD((pi+si)%2)}>{ords.map(([ref,qty])=><div key={ref} style={{ fontSize:12 }}><strong style={{ color:"#F0EBD8" }}>{ref}</strong><span style={{ color:"#9A8F6E", marginLeft:4 }}>×{qty}</span></div>)}</td>
                      <td style={TD((pi+si)%2)}><span style={{ color:col, fontWeight:700 }}>{fmt(st.girth)}mm</span></td>
                      <td style={TD((pi+si)%2)}><strong style={{ fontSize:14 }}>{fmt(st.length)}mm</strong></td>
                      <td style={TD((pi+si)%2)}><span style={{ color:col }}>{fmtM(st.runLength)}</span></td>
                      <td style={TD((pi+si)%2)}>{st.piecesPerRun>1?<Tag color="#38A169">×{st.piecesPerRun} cross-cut</Tag>:<span style={{ color:"#9A8F6E" }}>×1</span>}</td>
                      <td style={TD((pi+si)%2)}><Tag color={col}>×{st.strips}</Tag></td>
                      <td style={TD((pi+si)%2)}><strong style={{ fontSize:16, color:"#F0EBD8" }}>{st.qty}</strong></td>
                      {kgRate>0&&<td style={TD((pi+si)%2)}>
                        <div style={{ color:"#38A169", fontWeight:700 }}>{fmtKg(weightKg(st.girth,st.length*st.qty))}</div>
                        <div style={{ color:"#9A8F6E", fontSize:10 }}>{fmtKg(weightKg(st.girth,st.length))} ea</div>
                      </td>}
                      <td style={TD((pi+si)%2)}>
                        {isFirst
                          ? <div>
                              <span style={{ color:parseFloat(pass.wastePct)>15?"#F43F5E":"#38A169", fontWeight:700 }}>{fmt(pass.waste)}mm ({pass.wastePct}%)</span>
                              {kgRate>0&&<div style={{ fontSize:10, color:"#F43F5E", marginTop:2 }}>{fmtKg(weightKg(pass.waste,pass.runLength))}</div>}
                            </div>
                          : <span style={{ color:"#2A2A2A" }}>—</span>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Waste footer */}
        <div style={{ padding:"14px 20px", background:"#1A1A1A", borderTop:"1px solid #2C2400" }}>
          <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:10 }}>WASTE BY PASS</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {result.passes.map((pass,pi) => {
              const bad=parseFloat(pass.wastePct)>15, wKg=weightKg(pass.waste,pass.runLength);
              return (
                <div key={pi} style={{ background:"#0A0A0A", border:`1px solid ${bad?"#F43F5E33":"#2A2A2A"}`, borderRadius:7, padding:"8px 14px", minWidth:100 }}>
                  <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:1, marginBottom:3 }}>PASS {pass.num}</div>
                  <div style={{ fontWeight:700, color:bad?"#F43F5E":"#38A169", fontSize:14 }}>{fmt(pass.waste)}mm</div>
                  <div style={{ fontSize:10, color:"#9A8F6E" }}>{pass.wastePct}%</div>
                  {kgRate>0&&wKg!==null&&<div style={{ fontSize:10, color:"#F43F5E", marginTop:2 }}>{fmtKg(wKg)}</div>}
                </div>
              );
            })}
            <div style={{ background:"#0A0A0A", border:"1px solid #2C2400", borderRadius:7, padding:"8px 14px", minWidth:100 }}>
              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:1, marginBottom:3 }}>TOTAL WASTE</div>
              <div style={{ fontWeight:700, color:"#F0EBD8", fontSize:14 }}>{fmt(result.totalWaste)}mm</div>
              <div style={{ fontSize:10, color:"#9A8F6E" }}>avg {result.avgWaste}%</div>
              {kgRate>0&&<div style={{ fontSize:10, color:"#F43F5E", marginTop:2 }}>{fmtKg(calcWeights().wasteKg)}</div>}
            </div>
            {kgRate>0&&(
              <div style={{ background:"#0A110A", border:"1px solid #4ADE8033", borderRadius:7, padding:"8px 14px", minWidth:100 }}>
                <div style={{ fontSize:9, color:"#38A169", letterSpacing:1, marginBottom:3 }}>TOTAL PARTS</div>
                <div style={{ fontWeight:700, color:"#38A169", fontSize:14 }}>{fmtKg(calcWeights().pieceKg)}</div>
                {gauge&&<div style={{ fontSize:10, color:"#38A169" }}>{gauge}mm · {kgPerM}kg/m</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // HISTORY VIEW
  // ─────────────────────────────────────────────────────────────────────────────
  const HistoryView = (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:16, fontWeight:800, color:"#38A169", letterSpacing:2, marginBottom:4 }}>JOB HISTORY</div>
        <div style={{ fontSize:11, color:"#9A8F6E" }}>Saved jobs persist between sessions. Filter by date to see totals.</div>
      </div>
      <div style={{ background:"#111111", border:"1px solid #4ADE8033", borderRadius:10, padding:"16px 20px", marginBottom:20 }}>
        <div style={{ fontSize:10, color:"#38A169", letterSpacing:2, marginBottom:12 }}>FILTER BY DATE RANGE</div>
        <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
          {[["FROM","from"],["TO","to"]].map(([label,key]) => (
            <div key={key}>
              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>{label}</div>
              <input type="date" value={histRange[key]} onChange={e=>setHistRange(p=>({...p,[key]:e.target.value}))}
                style={{ background:"#0A0A0A", border:"1px solid #2C2400", borderRadius:6, padding:"7px 10px", color:"#F0EBD8", fontSize:12, fontFamily:"inherit", outline:"none" }} />
            </div>
          ))}
          <Btn onClick={()=>setHistRange({from:"",to:""})} style={{ marginTop:18 }}>CLEAR</Btn>
        </div>
        {(() => {
          const from=histRange.from?new Date(histRange.from):null;
          const to=histRange.to?new Date(histRange.to+"T23:59:59"):null;
          const filtered=history.filter(r=>{ const d=new Date(r.date); return(!from||d>=from)&&(!to||d<=to); });
          if (!filtered.length) return <div style={{ marginTop:14, fontSize:11, color:"#5A5040" }}>{history.length===0?"No jobs saved yet.":"No jobs in this date range."}</div>;
          const totWasteMm=filtered.reduce((s,r)=>s+(r.totalWasteMm||0),0);
          const totWasteKg=filtered.reduce((s,r)=>s+(r.totalWasteKg||0),0);
          const totPieceKg=filtered.reduce((s,r)=>s+(r.totalPieceKg||0),0);
          const totPcs=filtered.reduce((s,r)=>s+(r.totalPcs||0),0);
          const hasKg=filtered.some(r=>r.totalPieceKg!==null);
          return (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:10 }}>TOTALS — {filtered.length} JOB{filtered.length!==1?"S":""}{(from||to)?" IN RANGE":" (ALL TIME)"}</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10 }}>
                <Badge label="JOBS" value={filtered.length} color="#38A169" />
                <Badge label="TOTAL PIECES" value={fmt(totPcs)} />
                <Badge label="TOTAL WIDTH WASTE" value={`${fmt(Math.round(totWasteMm))}mm`} color={totWasteMm>5000?"#F43F5E":"#F5C518"} />
                {hasKg&&<Badge label="PIECE WEIGHT" value={`${totPieceKg.toFixed(1)}kg`} color="#38A169" />}
                {hasKg&&<Badge label="WASTE WEIGHT" value={`${totWasteKg.toFixed(1)}kg`} color="#F43F5E" />}
                {hasKg&&totPieceKg>0&&<Badge label="WASTE RATIO" value={`${((totWasteKg/(totPieceKg||1))*100).toFixed(1)}%`} color="#F5C518" />}
              </div>
            </div>
          );
        })()}
      </div>
      {history.length===0?(
        <div style={{ textAlign:"center", padding:"60px 0", color:"#2A2A2A" }}>
          <div style={{ fontSize:48 }}></div>
          <div style={{ fontSize:11, letterSpacing:3, marginTop:12 }}>NO SAVED JOBS YET</div>
        </div>
      ):(
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {history.map(rec => {
            const d=new Date(rec.date);
            const from=histRange.from?new Date(histRange.from):null;
            const to=histRange.to?new Date(histRange.to+"T23:59:59"):null;
            const inRange=(!from||d>=from)&&(!to||d<=to);
            return (
              <div key={rec.id} style={{ background:"#111111", border:`1px solid ${inRange?"#4ADE8033":"#2A2A2A"}`, borderLeft:`3px solid ${inRange?"#38A169":"#333333"}`, borderRadius:8, padding:"14px 18px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, opacity:(histRange.from||histRange.to)&&!inRange?0.35:1 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#F0EBD8", marginBottom:4 }}>{rec.name}</div>
                  <div style={{ fontSize:10, color:"#9A8F6E", marginBottom:8 }}>{d.toLocaleDateString("en-AU",{day:"2-digit",month:"short",year:"numeric"})} · {d.toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"})} · {rec.coilType} {rec.coilWidth}mm{rec.gauge?` · ${rec.gauge}mm · ${rec.kgPerM}kg/m`:""}</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {[
                      {l:"PASSES",v:rec.passes,c:"#F5C518"},
                      {l:"PIECES",v:fmt(rec.totalPcs),c:"#F0EBD8"},
                      {l:"AVG WASTE",v:`${rec.avgWastePct}%`,c:parseFloat(rec.avgWastePct)>15?"#F43F5E":"#38A169"},
                      {l:"WIDTH WASTE",v:`${fmt(Math.round(rec.totalWasteMm))}mm`,c:"#F5C518"},
                      ...(rec.totalPieceKg!==null?[{l:"PARTS",v:`${rec.totalPieceKg.toFixed(1)}kg`,c:"#38A169"}]:[]),
                      ...(rec.totalWasteKg!==null?[{l:"WASTE",v:`${rec.totalWasteKg.toFixed(1)}kg`,c:"#F43F5E"}]:[]),
                    ].map((s,i)=>(
                      <div key={i} style={{ background:"#0A0A0A", border:"1px solid #2C2400", borderRadius:5, padding:"5px 10px" }}>
                        <div style={{ fontSize:8, color:"#9A8F6E", letterSpacing:1, marginBottom:2 }}>{s.l}</div>
                        <div style={{ fontSize:13, fontWeight:700, color:s.c }}>{s.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <button onClick={()=>deleteRecord(rec.id)} style={{ background:"none", border:"1px solid #F43F5E44", borderRadius:5, color:"#F43F5E55", cursor:"pointer", padding:"5px 12px", fontSize:10, fontFamily:"inherit" }}
                  onMouseEnter={e=>{e.currentTarget.style.background="#F43F5E22";e.currentTarget.style.color="#F43F5E";}}
                  onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color="#F43F5E55";}}>DELETE</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // STOCK VIEW
  // ─────────────────────────────────────────────────────────────────────────────
  const StockView = (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:16, fontWeight:800, color:"#7CB9E8", letterSpacing:2, marginBottom:4 }}>COIL STOCK</div>
        <div style={{ fontSize:11, color:"#9A8F6E" }}>Track coil inventory by colour, type and gauge.</div>
      </div>

      {/* Warning threshold */}
      <div style={{ background:"#111111", border:"1px solid #F5C51833", borderRadius:10, padding:"12px 20px", marginBottom:20, display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, color:"#F5C518", fontWeight:700, letterSpacing:2 }}> LOW STOCK WARNING THRESHOLD</span>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <input type="number" value={stockSettings.warnKg} min={1}
            onChange={e=>updateSettings({...stockSettings,warnKg:Number(e.target.value)})}
            style={{ width:80, background:"#0A0A0A", border:"1px solid #F5C51844", borderRadius:6, padding:"6px 10px", color:"#F5C518", fontSize:14, fontFamily:"inherit", outline:"none", fontWeight:700 }} />
          <span style={{ fontSize:11, color:"#F5C518" }}>kg</span>
        </div>
        <span style={{ fontSize:10, color:"#9A8F6E" }}>Warn when remaining weight drops to or below this value</span>
      </div>

      {/* Stock summary */}
      {stock.length>0&&(
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:20 }}>
          <Badge label="COILS IN STOCK" value={stock.length} color="#7CB9E8" />
          <Badge label="TOTAL WEIGHT" value={`${stock.reduce((s,c)=>s+c.remaining,0).toFixed(1)}kg`} color="#38A169" />
          <Badge label="LOW STOCK ALERTS" value={lowStockCount} color={lowStockCount>0?"#F43F5E":"#38A169"} />
        </div>
      )}

      {/* Add coil form */}
      <div style={{ background:"#111111", border:"1px solid #38BDF844", borderRadius:10, padding:"18px 20px", marginBottom:20 }}>
        <div style={{ fontSize:10, color:"#7CB9E8", letterSpacing:3, marginBottom:14 }}>ADD COIL TO STOCK</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginBottom:12 }}>
          {[["COLOUR *","text",newCoil.colour,v=>setNewCoil(p=>({...p,colour:v})),"e.g. Sandstone"],["NOTES","text",newCoil.notes,v=>setNewCoil(p=>({...p,notes:v})),"e.g. Remnant"]].map(([label,type,val,set,ph],i)=>(
            <div key={i}>
              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>{label}</div>
              <input type={type} value={val} onChange={e=>set(e.target.value)} placeholder={ph}
                style={{ width:"100%", background:"#0A0A0A", border:"1px solid #2C2400", borderRadius:5, padding:"7px 10px", color:"#F0EBD8", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
            </div>
          ))}
          <div>
            <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>COIL TYPE *</div>
            <select value={newCoil.type} onChange={e=>setNewCoil(p=>({...p,type:e.target.value}))}
              style={{ width:"100%", background:"#0A0A0A", border:"1px solid #2C2400", borderRadius:5, padding:"7px 10px", color:"#F0EBD8", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}>
              <option value="coloursteel">Coloursteel</option>
              <option value="zinkalume">Zinkalume</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>GAUGE (mm) *</div>
            <NumInput val={newCoil.gauge} set={v=>setNewCoil(p=>({...p,gauge:v}))} placeholder="e.g. 0.55" />
          </div>
          <div>
            <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>WIDTH (mm)</div>
            <NumInput val={newCoil.width} set={v=>setNewCoil(p=>({...p,width:v}))} placeholder="auto" />
          </div>
          <div>
            <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>WEIGHT (kg) *</div>
            <NumInput val={newCoil.weightKg} set={v=>setNewCoil(p=>({...p,weightKg:v}))} placeholder="e.g. 250" />
          </div>
        </div>
        <Btn onClick={addCoil} color="#7CB9E8" fg="#000">＋ ADD TO STOCK</Btn>
      </div>

      {/* Stock list */}
      {stock.length===0?(
        <div style={{ textAlign:"center", padding:"60px 0", color:"#2A2A2A" }}>
          <div style={{ fontSize:48 }}></div>
          <div style={{ fontSize:11, letterSpacing:3, marginTop:12 }}>NO COILS IN STOCK</div>
          <div style={{ fontSize:10, color:"#5A5040", marginTop:6 }}>Add coils above to start tracking stock</div>
        </div>
      ):(
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {stock.map(coil=>{
            const isLow=coil.remaining>0&&coil.remaining<=stockSettings.warnKg;
            const isEmpty=coil.remaining<=0;
            const pct=Math.max(0,Math.min(100,(coil.remaining/coil.weightKg)*100));
            const barColor=isEmpty?"#F43F5E":isLow?"#F5C518":"#38A169";
            return (
              <div key={coil.id} style={{ background:"#111111", border:`1px solid ${isEmpty?"#F43F5E55":isLow?"#F5C51855":"#2A2A2A"}`, borderLeft:`3px solid ${barColor}`, borderRadius:8, padding:"14px 18px" }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
                      <span style={{ fontSize:15, fontWeight:800, color:"#F0EBD8" }}>{coil.colour}</span>
                      <Tag color={COIL_PRESETS[coil.type]?.color||"#9A8F6E"}>{COIL_PRESETS[coil.type]?.label||coil.type}</Tag>
                      <Tag color="#9A8F6E">{coil.gauge}mm</Tag>
                      <Tag color="#9A8F6E">{coil.width}mm wide</Tag>
                      {isEmpty&&<Tag color="#F43F5E">⚠ EMPTY</Tag>}
                      {!isEmpty&&isLow&&<Tag color="#F5C518">⚠ LOW STOCK</Tag>}
                    </div>
                    <div style={{ marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"#9A8F6E", marginBottom:4 }}>
                        <span>Remaining: <strong style={{ color:barColor, fontSize:13 }}>{coil.remaining.toFixed(1)}kg</strong></span>
                        <span>of {coil.weightKg.toFixed(1)}kg ({pct.toFixed(0)}%)</span>
                      </div>
                      <div style={{ height:8, background:"#2A2A2A", borderRadius:4, overflow:"hidden" }}>
                        <div style={{ width:`${pct}%`, height:"100%", background:barColor, borderRadius:4, transition:"width .3s" }} />
                      </div>
                      {isLow&&!isEmpty&&<div style={{ fontSize:9, color:"#F5C518", marginTop:3 }}>⚠ Below {stockSettings.warnKg}kg warning threshold</div>}
                    </div>
                    <div style={{ fontSize:10, color:"#9A8F6E" }}>
                      Added {new Date(coil.addedDate).toLocaleDateString("en-AU",{day:"2-digit",month:"short",year:"numeric"})}
                      {coil.notes?`  ·  ${coil.notes}`:""}
                      {coil.deductions?.length>0&&`  ·  ${coil.deductions.length} job deduction${coil.deductions.length!==1?"s":""}`}
                    </div>
                    {coil.deductions?.length>0&&(
                      <div style={{ marginTop:6 }}>
                        {coil.deductions.slice(-3).map((d,i)=>(
                          <div key={i} style={{ fontSize:9, color:"#5A5040" }}>
                            {new Date(d.date).toLocaleDateString("en-AU",{day:"2-digit",month:"short"})} — {d.jobName} — <span style={{ color:"#F43F5E" }}>−{d.kg.toFixed(1)}kg</span>
                          </div>
                        ))}
                        {coil.deductions.length>3&&<div style={{ fontSize:9, color:"#5A5040" }}>+{coil.deductions.length-3} more</div>}
                      </div>
                    )}
                  </div>
                  <button onClick={()=>removeCoil(coil.id)} style={{ background:"none", border:"1px solid #F43F5E44", borderRadius:5, color:"#F43F5E55", cursor:"pointer", padding:"5px 12px", fontSize:10, fontFamily:"inherit" }}
                    onMouseEnter={e=>{e.currentTarget.style.background="#F43F5E22";e.currentTarget.style.color="#F43F5E";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color="#F43F5E55";}}>REMOVE</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // DEDUCT MODAL
  // ─────────────────────────────────────────────────────────────────────────────
  const Modal = showModal && pendingJob && (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#111111", border:"1px solid #4ADE8055", borderRadius:12, padding:28, maxWidth:560, width:"100%", maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ fontSize:15, fontWeight:800, color:"#F0EBD8", marginBottom:6 }}> Review & Confirm — {pendingJob.jobName}</div>

        {pendingJob.totalKg!==null&&(
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:20 }}>
            <div style={{ background:"#1A1A1A", border:"1px solid #4ADE8033", borderRadius:7, padding:"10px 14px" }}>
              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>PIECE WEIGHT</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#38A169" }}>{pendingJob.pieceKg.toFixed(2)}kg</div>
            </div>
            <div style={{ background:"#1A1A1A", border:"1px solid #F43F5E33", borderRadius:7, padding:"10px 14px" }}>
              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>WASTE WEIGHT</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#F43F5E" }}>{pendingJob.wasteKg.toFixed(2)}kg</div>
            </div>
            <div style={{ background:"#1A1A1A", border:"1px solid #2C2400", borderRadius:7, padding:"10px 14px" }}>
              <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:2, marginBottom:4 }}>TOTAL DEDUCT</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#F0EBD8" }}>{pendingJob.totalKg.toFixed(2)}kg</div>
            </div>
          </div>
        )}

        {pendingJob.totalKg!==null&&(
          <>
            <div style={{ fontSize:10, color:"#9A8F6E", letterSpacing:2, marginBottom:10 }}>SELECT COIL TO DEDUCT FROM</div>
            {pendingJob.matchingCoils.length===0?(
              <div style={{ background:"#150A0A", border:"1px solid #F43F5E44", borderRadius:8, padding:"12px 16px", marginBottom:16, color:"#F43F5E", fontSize:11 }}>
                ⚠ No matching coil found (same type + gauge). Save without deducting stock below.
              </div>
            ):(
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
                {pendingJob.matchingCoils.map(coil=>{
                  const after=coil.remaining-pendingJob.totalKg;
                  const willWarn=after<=stockSettings.warnKg;
                  const willEmpty=after<=0;
                  return (
                    <div key={coil.id} style={{ background:"#1A1A1A", border:`1px solid ${willEmpty?"#F43F5E44":willWarn?"#F5C51844":"#2A2A2A"}`, borderRadius:8, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                      <div>
                        <div style={{ fontWeight:700, color:"#F0EBD8", fontSize:13 }}>{coil.colour} — {COIL_PRESETS[coil.type]?.label} {coil.gauge}mm</div>
                        <div style={{ fontSize:10, color:"#9A8F6E", marginTop:3 }}>
                          Current: <strong style={{ color:"#F0EBD8" }}>{coil.remaining.toFixed(1)}kg</strong> → After: <strong style={{ color:willEmpty?"#F43F5E":willWarn?"#F5C518":"#38A169" }}>{Math.max(0,after).toFixed(1)}kg</strong>
                          {willEmpty&&<span style={{ color:"#F43F5E", marginLeft:8 }}>⚠ COIL EMPTY</span>}
                          {!willEmpty&&willWarn&&<span style={{ color:"#F5C518", marginLeft:8 }}>⚠ LOW STOCK</span>}
                        </div>
                      </div>
                      <Btn onClick={()=>commitSaveJob(coil.id)} color="#38A169" fg="#000">✓ USE THIS COIL</Btn>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", flexWrap:"wrap" }}>
          <Btn onClick={()=>{setShowModal(false);setPendingJob(null);}}>CANCEL</Btn>
          <Btn onClick={()=>commitSaveJob(null)} color="#F5C518" fg="#000">SAVE WITHOUT DEDUCTING STOCK</Btn>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // SHELL
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:"100vh", background:"#0A0A0A", color:"#F0EBD8", fontFamily:"'Inter','Helvetica Neue',sans-serif", fontSize:13 }}>
      {/* Header */}
      <div style={{ background:"#111111", borderBottom:"2px solid #F5C518", padding:"14px 28px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:800, letterSpacing:3, color:accent }}>▪ COIL CUT OPTIMISER</div>
          <div style={{ fontSize:9, color:"#9A8F6E", letterSpacing:3, marginTop:2 }}>STEEL COIL CUTTING · WASTE MANAGEMENT · STOCK CONTROL</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          {/* Coil presets */}
          {Object.entries(COIL_PRESETS).map(([key,val])=>(
            <button key={key} onClick={()=>{setCoilKey(key);setCustomW("");setResult(null);}} style={{ padding:"6px 14px", borderRadius:6, fontFamily:"inherit", fontSize:11, fontWeight:700, letterSpacing:1, cursor:"pointer", border:`1px solid ${!customW&&coilKey===key?val.color:"#2A2A2A"}`, background:!customW&&coilKey===key?val.color+"22":"transparent", color:!customW&&coilKey===key?val.color:"#9A8F6E" }}>
              {val.label}<span style={{ display:"block", fontSize:9, fontWeight:400, marginTop:1 }}>{val.width}mm</span>
            </button>
          ))}
          {/* Custom width */}
          <div style={{ display:"flex", alignItems:"center", border:`1px solid ${customW?"#C4A1FF":"#2A2A2A"}`, borderRadius:6, overflow:"hidden", background:"#0A0A0A" }}>
            <div style={{ padding:"0 10px", fontSize:9, color:customW?"#C4A1FF":"#5A5040", letterSpacing:2, borderRight:"1px solid #2C2400" }}>CUSTOM</div>
            <input type="number" value={customW} min={100} max={2000} placeholder="e.g. 1150"
              onChange={e=>{setCustomW(e.target.value);setResult(null);}}
              style={{ width:90, background:"transparent", border:"none", color:customW?"#C4A1FF":"#9A8F6E", fontSize:12, fontFamily:"inherit", fontWeight:700, padding:"7px 8px", outline:"none" }} />
            <div style={{ padding:"0 8px", fontSize:9, color:customW?"#C4A1FF":"#5A5040" }}>mm</div>
            {customW&&<button onClick={()=>{setCustomW("");setResult(null);}} style={{ background:"none", border:"none", borderLeft:"1px solid #2C2400", color:"#ff1e1e", fontWeight:900, cursor:"pointer", padding:"0 8px", fontSize:16, lineHeight:"34px" }} onMouseEnter={e=>e.target.style.color="#F43F5E"} onMouseLeave={e=>e.target.style.color="#ff1e1e"}>×</button>}
          </div>
          {/* Gauge / kg/m */}
          <div style={{ display:"flex", alignItems:"center", border:`1px solid ${kgRate?"#4ADE8044":"#2A2A2A"}`, borderRadius:6, overflow:"hidden", background:"#0A0A0A" }}>
            <div style={{ padding:"0 8px", fontSize:9, color:kgRate?"#38A169":"#5A5040", letterSpacing:2, borderRight:"1px solid #2C2400" }}>GAUGE</div>
            <input type="number" value={gauge} min={0} step={0.01} placeholder="0.55"
              onChange={e=>setGauge(e.target.value)}
              style={{ width:60, background:"transparent", border:"none", color:kgRate?"#38A169":"#9A8F6E", fontSize:12, fontFamily:"inherit", padding:"7px 6px", outline:"none" }} />
            <div style={{ padding:"0 6px", fontSize:9, color:kgRate?"#38A16966":"#2A2A2A", borderLeft:"1px solid #2C2400", borderRight:"1px solid #2C2400" }}>mm</div>
            <div style={{ padding:"0 8px", fontSize:9, color:kgRate?"#38A169":"#5A5040", letterSpacing:2 }}>KG/M</div>
            <input type="number" value={kgPerM} min={0} step={0.1} placeholder="5.5"
              onChange={e=>setKgPerM(e.target.value)}
              style={{ width:60, background:"transparent", border:"none", color:kgRate?"#38A169":"#9A8F6E", fontSize:12, fontFamily:"inherit", padding:"7px 6px", outline:"none" }} />
            <div style={{ padding:"0 8px", fontSize:9, color:kgRate?"#38A16966":"#2A2A2A" }}>kg</div>
          </div>
          {/* Tabs */}
          <div style={{ display:"flex", flexWrap:"wrap", border:"1px solid #F5C51844", borderRadius:6, overflow:"hidden", marginLeft:4 }}>
            {[
              {k:"input",  l:"INPUT"},
              {k:"results",l:"CUTTING LIST",d:!result},
              {k:"history",l:"HISTORY"},
              {k:"stock",  l:"STOCK"},
            ].map(t=>(
              <button key={t.k} onClick={()=>{if(!t.d)setView(t.k);}} style={{ padding:"7px 14px", border:"none", fontFamily:"inherit", fontSize:11, fontWeight:700, letterSpacing:1, cursor:t.d?"default":"pointer", background:view===t.k?accent+"22":"transparent", color:view===t.k?accent:t.d?"#5A5040":"#9A8F6E" }}>
                {t.l}
                {t.k==="results"&&result&&<span style={{ marginLeft:4, background:accent, color:"#000", borderRadius:10, padding:"0 5px", fontSize:9 }}>{result.passes.length}</span>}
                {t.k==="history"&&history.length>0&&<span style={{ marginLeft:4, background:"#38A169", color:"#000", borderRadius:10, padding:"0 5px", fontSize:9 }}>{history.length}</span>}
                {t.k==="stock"&&lowStockCount>0&&<span style={{ marginLeft:4, background:"#F43F5E", color:"#fff", borderRadius:10, padding:"0 5px", fontSize:9 }}>⚠{lowStockCount}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding:"22px 28px", maxWidth:1200, margin:"0 auto" }}>
        {view==="input"?InputView:view==="results"?ResultsView:view==="history"?HistoryView:StockView}
      </div>
      {Modal}
    </div>
  );
}
